/**
 * JustEasySEO — Design tokens (Fase 1 M144).
 *
 * Deze module is de enige bron van waarheid voor kleuren, spacing, radii,
 * typografie en shadows. Tailwind (`tailwind.config.js`) leest deze tokens
 * via de mirror in `index.css` (:root custom properties) en dupliceert de
 * namen als utility-classes. Runtime-JS mag dit object direct importeren
 * (bv. voor de contrast-checker of inline styles).
 *
 * Design-brief:
 *  - Warm wit + petrol als hoofd. Mint en abrikoos ondersteunend.
 *  - Inter overal.
 *  - Rustige schaduwen, 12-16px hoekafronding, 8px spacing scale.
 */

export const colors = {
  // Basis
  bgCanvas:       "#F7F8F5", // pagina-achtergrond warm wit
  bgPanel:        "#FFFFFF", // kaarten, formulieren, zijbalk
  bgPanelRaised:  "#FDFDFB", // subtiel opgetild
  // Tekst
  textPrimary:    "#172C32", // inktblauw — koppen, body, cijfers
  textMuted:      "#627478", // secundaire tekst (grijsgroen)
  textSubtle:     "#6E7C7F", // bijschriften / hints (donker genoeg voor 3:1 op wit)
  textOnPetrol:   "#FFFFFF", // witte tekst op petrol
  textOnMint:     "#172C32", // donker op mint
  textOnApricot:  "#172C32", // donker op abrikoos
  // Primair
  accentPetrol:      "#087F8C", // hoofdkleur — knoppen, links, selectie
  accentPetrolHover: "#066A75", // hover / actief
  accentPetrolSoft:  "#087F8C1A", // 10% petrol voor hover-bg op licht
  // Steun
  accentMint:     "#E8F5EF", // samenvatting, geselecteerde nav
  accentApricot:  "#F4B183", // klein merkaccent
  // Randen
  borderHairline: "#DFE7E4", // scheidingslijnen
  borderSubtle:   "#EBEFED", // heel subtiel
  // Status
  posGreen:  "#217A52", // succes
  warnAmber: "#8A5B13", // waarschuwing (donker amber — voldoet contrast op wit)
  negRed:    "#B64242", // fout (rustig rood)
  infoBlue:  "#087F8C", // info = petrol (consistent)
};

export const spacing = {
  0:  "0",
  1:  "0.25rem",  // 4
  2:  "0.5rem",   // 8
  3:  "0.75rem",  // 12
  4:  "1rem",     // 16
  6:  "1.5rem",   // 24
  8:  "2rem",     // 32
  12: "3rem",     // 48
  16: "4rem",     // 64
};

export const radii = {
  sm:   "0.5rem",   // 8
  md:   "0.75rem",  // 12
  lg:   "1rem",     // 16
  pill: "9999px",
};

export const typography = {
  fontFamily: {
    sans:    'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    display: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    mono:    'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  },
  // Body 16px basis, page titles 28-32px desktop.
  sizes: {
    xs:   "0.75rem",   // 12
    sm:   "0.875rem",  // 14
    base: "1rem",      // 16 — body
    lg:   "1.125rem",  // 18
    xl:   "1.5rem",    // 24
    "2xl":"1.75rem",   // 28
    "3xl":"2rem",      // 32
  },
  weights: {
    regular:  400,
    medium:   500,
    semibold: 600,
    bold:     700,
  },
  lineHeights: {
    tight:    1.15,
    snug:     1.35,
    body:     1.5,
    relaxed:  1.65,
  },
};

export const shadows = {
  none: "none",
  xs:   "0 1px 2px 0 rgba(23, 44, 50, 0.04)",
  sm:   "0 2px 6px -1px rgba(23, 44, 50, 0.06), 0 1px 2px 0 rgba(23, 44, 50, 0.04)",
  focus:"0 0 0 3px rgba(8, 127, 140, 0.24)", // petrol focus-ring
};

const tokens = { colors, spacing, radii, typography, shadows };
export default tokens;
